import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classNames from "classnames";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { useNotification } from "../../contexts/NotificationContext";
import InventoryStatus from "views/inventory/status/InventoryStatus";
import Equipments from "./Equipments/Equipments";
import Locations from "./Locations/Locations";
import { useTranslation } from "react-i18next";

const InventoryParameters = ({ activeColor = "secondary", account, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(urlTab || "status");
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const tabPermissions = {
    "status": "inventory_parameters_status_read",
    "equipments": "inventory_parameters_equipments_read",
    // "locations": "inventory_parameters_locations_read",
  };

  const tabs = Object.keys(tabPermissions).filter(tab => {
    const permission = tabPermissions[tab];
    return account?.permissions?.[permission] === true;
  });

  useEffect(() => {
    if (urlTab) {
      const hasPermission = tabs.includes(urlTab);
      
      if (hasPermission) {
        setActiveTab(urlTab);
      } else {
        showNotification("danger", t('inventoryParameters.noPermissionForPage'));
        navigate('/dashboard');
        return;
      }
    } else if (tabs.length > 0) {
      setActiveTab(tabs[0]);
    } else {
      showNotification("danger", t('inventoryParameters.noPermissionForInventoryParameters'));
      navigate('/dashboard');
    }
  }, [urlTab, tabs, navigate, showNotification]);

  const getTabDisplayName = (tab) => {
    const tabNames = {
      "status": t('inventoryParameters.status'),
      "equipments": t('inventoryParameters.equipments'),
    };
    return tabNames[tab] || tab.toUpperCase();
  };

  const handleTabClick = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', tab);
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };

  return (
    <div
      className={classNames("content inventory-parameters-page", {
        "inventory-parameters-page--theme2": shellThemeVariant === "2",
        "theme2-admin-content-compact": shellThemeVariant === "2",
        "theme2-inventory-parameters-inset": shellThemeVariant === "2",
      })}
    >
      <style>
        {`
          .inventory-parameters-page .inventory-status-card-header.card-header,
          .inventory-parameters-page .equipments-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .inventory-parameters-page .inventory-status-card-header h2,
          .inventory-parameters-page .inventory-status-card-header h4,
          .inventory-parameters-page .equipments-card-header h2,
          .inventory-parameters-page .equipments-card-header h4 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .inventory-parameters-page .inventory-status-card-header .align-items-center,
          .inventory-parameters-page .equipments-card-header .align-items-center {
            margin-top: 3px;
          }
          .inventory-parameters-page .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
        `}
      </style>
      {/* Tab Menüsü ve İçerik */}
      <div className="w-100 inventory-parameters-detail-tabs">
        <div className="d-flex align-items-center justify-content-between">
          <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
            {tabs.map(tab => (
              <NavItem key={tab}>
                <NavLink
                  href="#"
                  className={activeTab === tab ? "active font-weight-bold" : "text-muted"}
                  style={{
                    background: "none",
                    border: "none",
                    // fontSize: 16,
                    // marginRight: 8,
                    position: "relative",
                    // fontWeight: 500,
                    cursor: 'pointer',
                    color: activeTab === tab ? activeColorStyle.borderColor : undefined,
                    fontWeight: activeTab === tab ? 700 : 500
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleTabClick(tab);
                  }}
                >
                  {getTabDisplayName(tab)}
                  {activeTab === tab && (
                    <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                  )}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
        <TabContent activeTab={activeTab}>
          <TabPane tabId={activeTab}>
                {activeTab === "status" && (
                  <InventoryStatus
                    permission={account?.permissions}
                    activeColor={activeColor}
                    shellThemeVariant={shellThemeVariant}
                  />
                )}
                {activeTab === "equipments" && (
                  <Equipments
                    permission={account?.permissions}
                    activeColor={activeColor}
                    shellThemeVariant={shellThemeVariant}
                  />
                )}
                {activeTab === "locations" && <Locations permission={account?.permissions} activeColor={activeColor} />}
          </TabPane>
        </TabContent>
      </div>
    </div>
  );
};

export default InventoryParameters; 